import {
  ExplanationRevisionStatusV2,
  ExpDocRevAgreementFormForSessionInSignFlowFragment,
} from 'src/lib/gql-client'

import {
  hasSigningNotRequiredAgreements,
  isDocRevCompleted,
} from './explanationDocRevisionUtils'
import { assertNever } from '../../../utils/assertNever'
import type { DocRevAllSignStatuses } from '../components/SignFlowScreen/SignFlowScreen'

/**
 * 署名実施状況（SignStatus）に表示するメッセージの種別。
 * - NotStarted: 署名がまだ開始されていない
 * - Signing: 署名を実施中
 * - AllCompleted: すべての同意書の署名が完了した
 * - RequiredCompletedOptionalRemaining: 必須同意書は完了したが、署名が完了していない任意同意書が残っている
 * - Completed: 同意完了後（署名済み）
 * - Rejected: 同意拒否された
 */
export type SignStatusMessageKind =
  | 'NotStarted'
  | 'Signing'
  | 'AllCompleted'
  | 'RequiredCompletedOptionalRemaining'
  | 'Completed'
  | 'Rejected'

type Params = {
  revStatus: ExplanationRevisionStatusV2
  isSignerMemberSet: boolean
  agreementForms: ExpDocRevAgreementFormForSessionInSignFlowFragment[]
  docRevSignStatusesMap: Map<string, DocRevAllSignStatuses>
}

// 1件でも署名中or署名済みがあるか
const hasAnySignAction = (
  docRevSignStatusesMap: Map<string, DocRevAllSignStatuses>,
): boolean => {
  return Array.from(docRevSignStatusesMap.values()).some(
    statuses =>
      statuses.Dr !== 'NotSigned' ||
      statuses.CRC !== 'NotSigned' ||
      statuses.Patient !== 'NotSigned',
  )
}

export const getSignStatusMessageKind = ({
  revStatus,
  isSignerMemberSet,
  agreementForms,
  docRevSignStatusesMap,
}: Params): SignStatusMessageKind | undefined => {
  if (agreementForms.length === 0) {
    return undefined
  }

  switch (revStatus) {
    case 'SignReady':
    case 'SignStarted':
      if (!isSignerMemberSet) {
        return undefined
      }
      return hasAnySignAction(docRevSignStatusesMap) ? 'Signing' : 'NotStarted'
    case 'SignCompleted':
      // 任意同意書の署名が開始されている（envelope あり）場合は実施中
      if (hasSigningNotRequiredAgreements(agreementForms)) {
        return 'Signing'
      }
      if (agreementForms.every(af => isDocRevCompleted(af))) {
        return 'AllCompleted'
      }
      // 必須同意書の署名は完了しているが、未着手の任意同意書が残っている
      return 'RequiredCompletedOptionalRemaining'
    case 'AgreementCompleted':
    case 'DeliveryCompleted':
      if (!hasAnySignAction(docRevSignStatusesMap)) {
        return undefined
      }
      return 'Completed'
    case 'AgreementRejected':
      return 'Rejected'
    case 'New':
    case 'SessionStarted':
    case 'Disabled':
    case 'Invalid':
    case 'Withdrawn':
      return undefined
    default:
      return assertNever(revStatus)
  }
}
